import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Phone } from "lucide-react";
import { API } from "@/App";

const SERVICES = [
  "Residential Cleanouts",
  "Construction Debris Removal",
  "Dump Runs & Full Truckload Hauling",
  "Other",
];

export default function QuoteForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    service_type: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service_type || !form.description) {
      toast.error("Please fill in Name, Phone, Service Type, and Description.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, photos: [] };
      await axios.post(`${API}/quotes`, payload);
      setSubmitted(true);
      toast.success("Got it — we'll call you shortly.");
      setForm({ name: "", phone: "", email: "", address: "", service_type: "", description: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid="quote-success"
        className="border-[3px] border-black bg-white p-8 md:p-12 shadow-brutal-lg text-center"
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center border-[3px] border-black bg-[#FFFFFF]">
          <CheckCircle2 className="h-8 w-8 text-black" />
        </div>
        <h3 className="font-display text-3xl md:text-4xl uppercase mb-3">Callback requested</h3>
        <p className="text-neutral-700 mb-6 max-w-xl mx-auto">
          Thanks — we&apos;ve got your details. We&apos;ll call you shortly to go over the job and give you a fair quote by phone. Need it faster? Call{" "}
          <a href="tel:+19733069206" className="font-bold text-[#0A0A0A] underline">
            (973) 306-9206
          </a>
          .
        </p>
        <button
          onClick={() => setSubmitted(false)}
          data-testid="quote-reset-btn"
          className="border-[3px] border-black bg-black text-white px-6 py-3 font-bold uppercase tracking-wider shadow-brutal-sm btn-brutal-hover"
        >
          Send Another
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full border-[3px] border-black bg-white px-4 py-3 text-black placeholder:text-neutral-400 focus:outline-none focus:ring-0 focus:border-[#0A0A0A] font-medium";

  return (
    <form
      onSubmit={onSubmit}
      data-testid="quote-form"
      className="border-[3px] border-black bg-white p-6 md:p-10 shadow-brutal-lg"
    >
      <div className="mb-5 flex items-center gap-3 border-[3px] border-black bg-[#FFFFFF] p-3">
        <Phone className="h-5 w-5 shrink-0" />
        <p className="text-sm font-medium leading-snug">
          <span className="font-bold uppercase">Heads up:</span> We give estimates by phone, not from photos. Fill this out and we&apos;ll call you back to talk through the job.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">Full Name *</label>
          <input
            data-testid="quote-input-name"
            name="name"
            value={form.name}
            onChange={onChange}
            placeholder="John Smith"
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">Phone *</label>
          <input
            data-testid="quote-input-phone"
            name="phone"
            value={form.phone}
            onChange={onChange}
            placeholder="(555) 123-4567"
            className={inputCls}
            required
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">Email</label>
          <input
            data-testid="quote-input-email"
            name="email"
            type="email"
            value={form.email}
            onChange={onChange}
            placeholder="you@example.com"
            className={inputCls}
          />
        </div>
        <div>
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">Job Address</label>
          <input
            data-testid="quote-input-address"
            name="address"
            value={form.address}
            onChange={onChange}
            placeholder="123 Main St, Town, NJ"
            className={inputCls}
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">Service Needed *</label>
          <select
            data-testid="quote-input-service"
            name="service_type"
            value={form.service_type}
            onChange={onChange}
            className={inputCls}
            required
          >
            <option value="">Select a service…</option>
            {SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-bold uppercase tracking-widest mb-2">
            Describe The Job *
          </label>
          <textarea
            data-testid="quote-input-description"
            name="description"
            value={form.description}
            onChange={onChange}
            rows={4}
            placeholder="e.g. Full garage cleanout, mostly old furniture and boxes. Need it done this weekend."
            className={inputCls}
            required
          />
        </div>
      </div>

      {/* Pricing note */}
      <div className="mt-6 border-[3px] border-black bg-black text-white p-4">
        <p className="text-sm font-medium leading-relaxed">
          <span className="font-bold uppercase text-[#FFFFFF]">Pricing note:</span> Construction debris (drywall, tile, flooring, cabinets, etc.) is priced at our
          standard truckload rate <span className="font-bold text-[#FFFFFF]">+ $50 or more</span> due to heavier materials and dump fees.
        </p>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="quote-submit-btn"
        className="mt-6 w-full flex items-center justify-center gap-2 border-[3px] border-black bg-[#0A0A0A] px-6 py-4 text-lg font-bold uppercase tracking-wider text-white shadow-brutal btn-brutal-hover disabled:opacity-70"
      >
        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
        {submitting ? "Sending…" : "Request A Callback"}
      </button>
      <p className="mt-3 text-center text-xs uppercase tracking-widest text-neutral-500">
        Fast reply. Fair pricing. No obligation.
      </p>
    </form>
  );
}
